import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { MetroToastService } from '../../shared/services/metro-toast.service';
import { ConfirmationModalService } from '../../shared/services/confirmation-modal.service';
import { IdentityUserService } from '@abp/ng.identity/proxy';
import { SommelierService } from '@proxy/sommeliers';
import { WineDto, WineService } from '@proxy/wines';
import { OrderService } from '@proxy/orders';
import { distinctUntilChanged } from 'rxjs';
import { forkJoin } from 'rxjs';
import { CreateOrderWithItemsDto, CreateOrderItemDto, StockStatus, CreateOrderResponseDto, StockValidationResultDto } from '@proxy/orders';
import { ColppyService } from '@proxy/colppy';
import { ColppyPriceListDto, ColppyPriceListItemDto } from '@proxy/colppy/dto';
import { PickUpService, PickUpDto } from '@proxy/pick-ups';
import { LocalizationService, HttpWaitService } from '@abp/ng.core';
import { CouponValidationResultDto, CouponItemValidationDto } from '@proxy/coupons/models';
import { CouponInputComponent } from '../../shared/components/coupon-input/coupon-input.component';

@Component({
  selector: 'app-create-order',
  templateUrl: './create-order.component.html',
  standalone: false,
})
export class CreateOrderComponent implements OnInit {
  @ViewChild(CouponInputComponent) couponInputRef?: CouponInputComponent;

  form: FormGroup;
  sommeliers: Array<{ value: number; label: string }> = [];
  users: Array<{ value: string; label: string }> = [];

  wines: WineDto[] = [];
  pickups: PickUpDto[] = [];
  pickUpsOptions: Array<{ value: string; label: string }> = [];
  wineOptions: Array<{ value: number; label: string }> = [];

  priceLists: Array<{ value: number; label: string }> = [];
  isLoading = false;
  isSaving = false;
  currentPriceListItems: ColppyPriceListItemDto[] = [];
  // Mapa para buscar rápidamente si un vino está en la lista de precios
  priceListItemsMap: Map<string, ColppyPriceListItemDto> = new Map();

  // Cupón aplicado al pedido (opcional). Clave de sessionStorage propia (no la que usa el
  // checkout de cliente) para que aplicar un cupón acá no contamine ni se contamine con el carrito.
  readonly couponPersistKey = 'backofficeAppliedCoupon';
  appliedCouponCode: string | null = null;
  private appliedCouponContext = '';
  appliedCouponDiscount = 0;
  orderTotal = 0;
  couponItems: CouponItemValidationDto[] = [];
  // Mapa Sommelier.id -> Sommelier.userId (Guid). CouponValidationDto.SommelierId espera el
  // Guid del usuario del sommelier, no el id numérico que usa el resto del form de admin.
  private sommelierUserIdById: Map<number, string> = new Map();
  couponSommelierId: string | undefined;
  // Se incrementa en cada selección de lista de precios; una respuesta cuyo número ya no coincide
  // es de una selección anterior y se descarta (evita que una respuesta tardía de A pise a B).
  private priceListRequestSeq = 0;

  deliveryMethodOptions = [
    { value: 'pickup', label: '::Pickup' },
    { value: 'meetwithsommelier', label: '::MeetWithSommelier' },
    { value: 'shipping', label: '::Shipping' },
  ];

  paymentMethodOptions = [
    { value: 'bank', label: '::BankTransfer' },
    { value: 'mercadopago', label: '::MercadoPago' },
    { value: 'cash', label: '::Cash' },
  ];

  constructor(
    private fb: FormBuilder,
    private pickUpService: PickUpService,
    private sommelierService: SommelierService,
    private userService: IdentityUserService,
    private wineService: WineService,
    private orderService: OrderService,
    private colppyService: ColppyService,
    private toaster: MetroToastService,
    private confirmationService: ConfirmationModalService,
    private router: Router,
    private localization: LocalizationService,
    private httpWaitService: HttpWaitService
  ) { }

  ngOnInit(): void {
    this.buildForm();
    this.loadData();
  }

  // Al hacer clic en Guardar el form re-emite sommelier/cliente/entrega/lista con el MISMO valor. Sin distinctUntilChanged eso
  // borraba el cupón y el punto de retiro justo antes de enviar la orden (winepassport#394).
  buildForm(): void {
    this.form = this.fb.group({
      sommelierId: [null, Validators.required],
      userId: [null, Validators.required],
      shippingAddressLine: ['', Validators.required], // Required for Pickup (Warehouse) & Shipping (Address)
      city: [''],
      state: [''],
      postalCode: [''],
      country: ['Argentina'],
      notes: [''],
      paymentMethod: ['bank', Validators.required],
      deliveryMethod: ['pickup', Validators.required],
      priceListId: [null, Validators.required],
      orderItems: this.fb.array([], Validators.minLength(1))
    });

    this.updateDeliveryValidators('pickup');

    // Listener for delivery method changes
    this.form.get('deliveryMethod')!.valueChanges.pipe(distinctUntilChanged()).subscribe(method => {
      this.updateDeliveryValidators(method);
    });

    // Listener para cuando cambia la lista de precios
    this.form.get('priceListId')!.valueChanges.pipe(distinctUntilChanged()).subscribe(priceListId => {
      console.log('PriceListId changed:', priceListId, 'Type:', typeof priceListId);
      // Ejecutar siempre, incluso si es null o undefined, para manejar la deselección
      this.onPriceListSelected(priceListId);
    });

    this.form.get('sommelierId')!.valueChanges.pipe(distinctUntilChanged()).subscribe(sommelierId => {
      this.loadPickUps(sommelierId);
      this.couponSommelierId = this.sommelierUserIdById.get(Number(sommelierId));
      // El sommelier participa de la validación del cupón (restricciones por sommelier):
      // un cupón ya aplicado quedaría validado contra un sommelier que ya no es el elegido.
      this.invalidateCouponIfContextChanged();
    });

    // El cliente también participa de la validación del cupón (ya usado / restringido por cliente).
    this.form.get('userId')!.valueChanges.pipe(distinctUntilChanged()).subscribe(() => this.invalidateCouponIfContextChanged());

    this.orderItems.valueChanges.subscribe(() => {
      this.updateCouponContext();
      // Vino, cantidad o precio cambiados invalidan cualquier cupón ya aplicado: se validó
      // contra un total/ítems que ya no son los actuales.
      this.invalidateCouponIfContextChanged();
    });
  }

  /** Recalcula el total y los ítems que ve el cupón a partir del form actual. */
  private updateCouponContext(): void {
    const items = this.orderItems.getRawValue() as Array<{ wineId: number; quantity: number; unitPrice: number }>;
    this.couponItems = items
      .filter(i => i.wineId && i.quantity)
      .map(i => ({ wineId: i.wineId, unitPrice: i.unitPrice || 0, quantity: i.quantity }));
    this.orderTotal = this.couponItems.reduce((sum, i) => sum + i.unitPrice * i.quantity, 0);
  }

  private clearAppliedCoupon(): void {
    this.appliedCouponCode = null;
    this.appliedCouponDiscount = 0;
  }

  /** Invalida cualquier cupón ya aplicado: limpia el estado del padre, el sessionStorage propio de
   * este form y el estado visual de <app-coupon-input> (que si no, seguiría mostrando "aplicado"
   * y podría restaurarlo desde sessionStorage al recrearse). Llamar ante cualquier cambio en los
   * datos que participan de la validación del cupón: lista de precios, sommelier, vinos/cantidades. */
  private invalidateCoupon(): void {
    this.clearAppliedCoupon();
    sessionStorage.removeItem(this.couponPersistKey);
    this.couponInputRef?.reset();
  }

  onCouponApplied(result: CouponValidationResultDto): void {
    this.appliedCouponCode = result.coupon?.code ?? null;
    this.appliedCouponDiscount = result.discountAmount ?? 0;
    this.appliedCouponContext = this.couponContextSignature();
  }

  /** Foto de lo que participa de la validación del cupón (sommelier, cliente, lista, vinos/cantidades/precios). */
  private couponContextSignature(): string {
    const v = this.form.getRawValue();
    return JSON.stringify([
      v.sommelierId, v.userId, v.priceListId,
      (v.orderItems || []).map((i: any) => [i.wineId, i.quantity, i.unitPrice])
    ]);
  }

  /** winepassport#394: solo invalida el cupón si cambió algo que de verdad lo afecta. Antes cualquier emisión
   * del form (aunque el valor fuera el mismo) lo borraba en silencio y la orden se guardaba sin descuento. */
  private invalidateCouponIfContextChanged(): void {
    if (!this.appliedCouponCode) return;
    if (this.couponContextSignature() === this.appliedCouponContext) return;
    this.invalidateCoupon();
    this.toaster.warn('El pedido cambió y el cupón se quitó. Volvé a aplicarlo para que se descuente.');
  }

  onCouponRemoved(): void {
    this.clearAppliedCoupon();
  }

  updateDeliveryValidators(method: string) {
    const addressControls = ['city', 'state', 'postalCode', 'country'];
    const shippingAddressLine = this.form.get('shippingAddressLine');

    if (method === 'shipping') {
      // Address fields required
      addressControls.forEach(controlName => {
        this.form.get(controlName)?.setValidators([Validators.required]);
        this.form.get(controlName)?.updateValueAndValidity();
      });
      shippingAddressLine?.setValidators([Validators.required]);
    } else if (method === 'pickup') {
      addressControls.forEach(controlName => {
        this.form.get(controlName)?.clearValidators();
        this.form.get(controlName)?.updateValueAndValidity();
      });
      shippingAddressLine?.setValidators([Validators.required]);
    } else {
      addressControls.forEach(controlName => {
        this.form.get(controlName)?.clearValidators();
        this.form.get(controlName)?.updateValueAndValidity();
      });
      shippingAddressLine?.clearValidators();
    }
    shippingAddressLine?.updateValueAndValidity();
  }

  loadPickUps(sommelierId: string) {
    this.pickups = [];
    this.pickUpsOptions = [];

    if (this.form.get('deliveryMethod')?.value === 'pickup') {
      this.form.get('shippingAddressLine')?.setValue(null);
    }

    if (sommelierId) {
      const id = Number(sommelierId);
      if (!isNaN(id)) {
        this.sommelierService.getPickUpsBySommelierId(id).subscribe({
          next: (pickups) => {
            this.pickups = pickups;
            this.pickUpsOptions = pickups
              .filter(p => p.colppyWarehouseName && p.colppyWarehouseName.trim() !== '')
              .map(p => ({
                value: p.colppyWarehouseName!,
                label: `${p.name} - ${p.address}`
              }));
          },
          error: (err) => console.error('Error loading pickups:', err)
        });
      }
    }
  }

  get orderItems(): FormArray {
    return this.form.get('orderItems') as FormArray;
  }

  addOrderItem() {
    const group = this.fb.group({
      wineId: [null, Validators.required],
      quantity: [1, Validators.required],
      unitPrice: [{ value: 0, disabled: true }, [Validators.required, Validators.min(0.01)]], // Readonly por defecto
      wineName: [''], // Hidden, for DTO
      externalCode: ['']
    });

    group.get('wineId')!.valueChanges.subscribe(wineId => {
      this.onWineSelected(wineId, group);
    });

    this.orderItems.push(group);
  }


  private onWineSelected(wineId: number, group: FormGroup): void {
  const wine = this.wines.find(w => w.id === wineId);
  if (!wine) return;

  group.patchValue({
    wineName: wine.name,
    externalCode: wine.externalCode
  });

  if (this.currentPriceListItems.length > 0) {
    const matchingItem = this.currentPriceListItems.find(item => {
      if (wine.idItemColppy && item.id.toString() === wine.idItemColppy) return true;
      if (wine.externalCode && item.codigo && wine.externalCode === item.codigo) return true;
      return false;
    });

    const priceToSet = matchingItem
      ? Math.round(matchingItem.sale_price * 1.21 * 100) / 100  // Lista: sin IVA → con IVA
      : wine.price;

    const unitPriceControl = group.get('unitPrice');
    unitPriceControl?.enable({ emitEvent: false });
    unitPriceControl?.setValue(priceToSet, { emitEvent: false });
    unitPriceControl?.disable({ emitEvent: false });
  } else {
    group.get('unitPrice')?.enable({ emitEvent: false });
    group.get('unitPrice')?.setValue(wine.price, { emitEvent: false });
    group.get('unitPrice')?.disable({ emitEvent: false });
  }
}

  removeOrderItem(index: number): void {
    this.orderItems.removeAt(index);
  }

  loadData(): void {
    this.isLoading = true;
    forkJoin({
      sommeliers: this.sommelierService.getList({ maxResultCount: 1000 }), // TODO: Handle pagination properly
      wines: this.wineService.getList({ maxResultCount: 1000 }), // TODO: Search/Autocomplete
      priceLists: this.colppyService.getPriceLists(),
      // users: this.userService.getList({ maxResultCount: 1000 }) // Fetching all users might be heavy. Ideally use autocomplete.
      // For now, let's try fetching users. If IdentityUserService is not available via proxy, we might need a different approach.
    }).subscribe({
      next: (res) => {
        this.sommeliers = res.sommeliers.items.map(s => ({
          value: s.id,
          label: s.fullName || `${s.name} ${s.surname}`
        }));
        this.sommelierUserIdById.clear();
        res.sommeliers.items.forEach(s => {
          if (s.userId) {
            this.sommelierUserIdById.set(s.id, s.userId);
          }
        });

        this.wines = res.wines.items;

        this.wineOptions = this.wines.map(w => ({
          value: w.id,
          label: w.name
        }));

        this.priceLists = res.priceLists.map(s => ({
          value: s.id,
          label: `${s.description} ${s.iso}`
        }));
        ;

        // Users loading deferred to a separate call or autocomplete
        this.loadUsers();
        this.isLoading = false;
      },
      error: (err) => {
        this.toaster.error('Error loading data');
        this.isLoading = false;
      }
    });
  }

  loadUsers(): void {
    // Assuming IdentityUserService has a getList method
    this.userService.getList({ maxResultCount: 1000 }).subscribe(res => {
      this.users = res.items.map(u => ({
        value: u.id,
        label: `${u.name} ${u.surname} (${u.email})`
      }));

    });
  }

  onUserChange(userId: string) {
    const user = this.users.find(u => u.value === userId);
    if (user) {
      // Auto-fill address if available in user profile?
      // IdentityUserDto usually doesn't have address.
      // So we skip auto-fill for now unless we have extra properties.
    }
  }

  onPriceListSelected(priceListId: number | string | null | undefined): void {
    // Cambiar de lista de precios invalida el total sobre el que se validó el cupón anterior.
    // Se limpia también sessionStorage de forma síncrona, ANTES de que el @for de la vista
    // recree <app-coupon-input> (track por priceListId): si no, la nueva instancia se monta y
    // restaura desde sessionStorage el mismo cupón que se está invalidando.
    const hadCoupon = !!this.appliedCouponCode;
    this.invalidateCoupon();
    if (hadCoupon) {
      this.toaster.warn('Cambió la lista de precios y el cupón se quitó. Volvé a aplicarlo para que se descuente.');
    }

    // Cada selección invalida las respuestas pendientes de una selección anterior: si el admin
    // elige A y enseguida B, una respuesta tardía de A ya no debe pisar el estado de B.
    const requestSeq = ++this.priceListRequestSeq;
    const isStale = () => requestSeq !== this.priceListRequestSeq;

    // Si se deselecciona la lista de precios (null, undefined, o cadena vacía), restaurar precios por defecto
    if (priceListId === null || priceListId === undefined || priceListId === '' || priceListId === 0 ||
      (typeof priceListId === 'string' && priceListId.trim() === '')) {
      this.currentPriceListItems = [];
      this.priceListItemsMap.clear();
      this.restoreDefaultPrices();
      this.updateCouponContext();
      return;
    }

    // Convertir a number si es string
    const idToSearch = typeof priceListId === 'string' ? Number(priceListId) : priceListId;

    if (isNaN(idToSearch) || idToSearch <= 0) {
      this.currentPriceListItems = [];
      this.priceListItemsMap.clear();
      this.restoreDefaultPrices();
      this.updateCouponContext();
      return;
    }

    // Llamar al servicio para obtener los items de la lista de precios desde el backend
    this.colppyService.getPriceListItems(idToSearch).subscribe({
      next: (items: ColppyPriceListItemDto[]) => {
        if (isStale()) return;

        // Guardar los items actuales
        this.currentPriceListItems = items;

        // Crear mapa para búsqueda rápida
        this.priceListItemsMap.clear();
        items.forEach(item => {
          // Usar id como string
          if (item.id) {
            this.priceListItemsMap.set(`id_${item.id}`, item);
          }
          // Usar código
          if (item.codigo) {
            this.priceListItemsMap.set(`code_${item.codigo}`, item);
          }
        });

        // Actualizar precios de los vinos en el formulario
        this.updateWinePrices(items);
        // updateWinePrices usa setValue({emitEvent:false}) para no disparar validaciones de
        // más — pero eso también significa que orderItems.valueChanges no corre, así que hay
        // que recalcular a mano el total/items que ve el cupón.
        this.updateCouponContext();
      },
      error: (err) => {
        if (isStale()) return;
        console.error('Error al obtener items de lista de precios desde el backend:', err);
        this.toaster.error('Error al cargar lista de precios');
        this.currentPriceListItems = [];
        this.priceListItemsMap.clear();
      }
    });
  }

  private restoreDefaultPrices(): void {
    // Iterar sobre todos los items de la orden en el formulario
    this.orderItems.controls.forEach((itemControl) => {
      const wineId = itemControl.get('wineId')?.value;

      if (!wineId) {
        return; // No hay vino seleccionado en este item
      }

      // Buscar el vino en la lista de vinos cargados
      const wine = this.wines.find(w => w.id === wineId);

      if (!wine) {
        return;
      }

      // Restaurar el precio por defecto del vino
      const unitPriceControl = itemControl.get('unitPrice');
      if (unitPriceControl) {
        // Usar patchValue con emitEvent para asegurarnos de que se actualiza incluso si está disabled
        const currentValue = unitPriceControl.value;
        const defaultPrice = wine.price || 0;

        // Si el precio actual es diferente del precio por defecto, restaurarlo
        if (currentValue !== defaultPrice) {
          // Habilitar temporalmente el control, actualizar el valor, y volver a deshabilitarlo
          unitPriceControl.enable({ emitEvent: false });
          unitPriceControl.setValue(defaultPrice, { emitEvent: false });
          unitPriceControl.disable({ emitEvent: false });
          console.log(`Precio restaurado para vino ${wine.name}: ${defaultPrice} (antes: ${currentValue})`);
        }
      }
    });
  }

  private updateWinePrices(priceListItems: ColppyPriceListItemDto[]): void {
    // Iterar sobre todos los items de la orden en el formulario
    this.orderItems.controls.forEach((itemControl, index) => {
      const wineId = itemControl.get('wineId')?.value;

      if (!wineId) {
        return; // No hay vino seleccionado en este item
      }

      // Buscar el vino en la lista de vinos cargados
      const wine = this.wines.find(w => w.id === wineId);

      if (!wine) {
        console.warn(`No se encontró el vino con ID ${wineId}`);
        return;
      }

      // Intentar hacer match con el item de la lista de precios
      // 1. Por idItemColppy (comparar como string)
      // 2. Por externalCode con codigo
      const matchingItem = priceListItems.find(item => {
        // Match por idItemColppy
        if (wine.idItemColppy && item.id.toString() === wine.idItemColppy) {
          return true;
        }
        // Match por externalCode
        if (wine.externalCode && item.codigo && wine.externalCode === item.codigo) {
          return true;
        }
        return false;
      });

      const unitPriceControl = itemControl.get('unitPrice');
      if (!unitPriceControl) {
        return;
      }

      if (matchingItem) {
        const currentValue = unitPriceControl.value;
        // El precio de lista viene sin IVA, sumarle 21%
        const newPrice = Math.round(matchingItem.sale_price * 1.21 * 100) / 100;

        if (currentValue !== newPrice) {
          unitPriceControl.enable({ emitEvent: false });
          unitPriceControl.setValue(newPrice, { emitEvent: false });
          unitPriceControl.disable({ emitEvent: false });
        }
      }else {
        // Si no está en la lista de precios, mantener el precio por defecto del vino
        const currentValue = unitPriceControl.value;
        const defaultPrice = wine.price || 0;

        if (!currentValue || currentValue === 0) {
          // Solo actualizar si no tiene precio o es 0
          unitPriceControl.enable({ emitEvent: false });
          unitPriceControl.setValue(defaultPrice, { emitEvent: false });
          unitPriceControl.disable({ emitEvent: false });
        }
        console.warn(`No se encontró precio en lista de precios para vino: ${wine.name} (ID: ${wine.idItemColppy}, Código: ${wine.externalCode}). Se mantiene precio por defecto: ${defaultPrice}`);
      }
    });
  }

  /**
   * Verifica si un vino está en la lista de precios actual
   */
  isWineInPriceList(wineId: number): boolean {
    if (!wineId || this.priceListItemsMap.size === 0) {
      return false;
    }

    const wine = this.wines.find(w => w.id === wineId);
    if (!wine) {
      return false;
    }

    // Verificar por idItemColppy
    if (wine.idItemColppy) {
      if (this.priceListItemsMap.has(`id_${wine.idItemColppy}`)) {
        return true;
      }
    }

    // Verificar por externalCode
    if (wine.externalCode) {
      if (this.priceListItemsMap.has(`code_${wine.externalCode}`)) {
        return true;
      }
    }

    return false;
  }

  backToOrders(): void {
    this.router.navigate(['/backoffice/orders']);
  }

  save(): void {
    // Si <app-coupon-input> muestra un cupón aplicado, el que se envía tiene que ser ese (una sola fuente de verdad).
    const shownCoupon = this.couponInputRef?.appliedCoupon();
    if (shownCoupon?.valid && !this.appliedCouponCode) {
      this.onCouponApplied(shownCoupon);
    }

    if (this.form.invalid) {
      console.log('Form is invalid. Controls status:');
      Object.keys(this.form.controls).forEach(key => {
        const control = this.form.get(key);
        if (control.invalid) {
          console.log(`- ${key}:`, control.errors);
          control.markAsTouched();
          if (control instanceof FormArray) {
            control.controls.forEach((c, i) => {
              if (c.invalid) console.log(`  - Item ${i}:`, (c as FormGroup).controls);
              c.markAsTouched();
            });
          }
        }
      });
      this.toaster.warn('::PleaseCheckFormErrors');
      return;
    }

    const formValue = this.form.getRawValue(); // Use getRawValue to get disabled fields if any

    // Calcular el total de la orden
    const total = formValue.orderItems.reduce((sum: number, item: any) => {
      return sum + (item.quantity * item.unitPrice);
    }, 0);

    // Obtener el nombre de la lista de precios
    const priceListId = formValue.priceListId;
    const selectedPriceList = this.priceLists.find(pl => pl.value === priceListId);
    const priceListName = selectedPriceList ? selectedPriceList.label : 'No especificada';

    const formatCurrency = (value: number) =>
      new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(value);

    // Mostrar confirmación. El modal renderiza este mensaje como HTML ([innerHTML] en
    // MetroConfirmationComponent) — cualquier valor dinámico que no sea un número formateado por
    // nosotros (como el código de cupón, que viene del backend sin restricción de caracteres)
    // tiene que escaparse antes de interpolarlo, para no abrir la puerta a HTML/script inyectado.
    const finalTotal = total - this.appliedCouponDiscount;
    const couponLine = this.appliedCouponCode
      ? `<br><strong>Cupón aplicado:</strong> ${this.escapeHtml(this.appliedCouponCode)} (descuento: ${formatCurrency(this.appliedCouponDiscount)})<br>
         <strong>Total final:</strong> ${formatCurrency(finalTotal)}`
      : '';

    const message = `¿Está seguro de crear la orden?<br><br>
      <strong>${this.appliedCouponCode ? 'Subtotal' : 'Total de la orden'}:</strong> ${formatCurrency(total)}<br>
      <strong>Lista de precios:</strong> ${priceListName}${couponLine}`;

    this.confirmationService.confirm({
      title: 'Confirmar creación de orden',
      message: message,
      type: 'warning',
      confirmText: 'Confirmar',
      cancelText: 'Cancelar',
      showCancelButton: true
    }).subscribe(confirmed => {
      if (confirmed) {
        this.createOrder(formValue);
      }
    });
  }

  private createOrder(formValue: any): void {
    this.isLoading = true;
    this.isSaving = true;

    // Map to DTO
    let shippingAddressLine = formValue.shippingAddressLine;
    let notes = formValue.notes;


    if (formValue.deliveryMethod === 'shipping') {
      if (!this.pickups || this.pickups.length === 0) {
        this.toaster.error('El sommelier seleccionado no tiene puntos de retiro configurados. No se puede realizar el envío.');
        this.isLoading = false;
        this.isSaving = false;
        return;
      }

      shippingAddressLine = formValue.shippingAddressLine;

      const fullAddress = [
        formValue.shippingAddressLine,
        formValue.city,
        formValue.state,
        formValue.postalCode,
        formValue.country
      ]
        .filter(x => !!x)
        .join(', ');

      shippingAddressLine = fullAddress;
    } else if (formValue.deliveryMethod === 'meetwithsommelier') {
      // Similar validation for meetwithsommelier
      if (!this.pickups || this.pickups.length === 0) {
        this.toaster.error('El sommelier seleccionado no tiene puntos de retiro configurados.');
        this.isLoading = false;
        this.isSaving = false;
        return;
      }
      if (this.pickups.length > 0 && this.pickups[0].colppyWarehouseName) {
        shippingAddressLine = this.pickups[0].colppyWarehouseName;
      }
    }

    const input: CreateOrderWithItemsDto = {
      sommelierId: formValue.sommelierId,
      userId: formValue.userId,
      shippingAddressLine: shippingAddressLine,
      notes: notes,
      paymentMethod: formValue.paymentMethod,
      deliveryMethod: formValue.deliveryMethod,
      priceListId: formValue.priceListId,
      priceListName: formValue.priceListId
        ? this.priceLists.find(p => p.value === Number(formValue.priceListId))?.label
        : undefined,
      couponCode: this.appliedCouponCode || undefined,
      orderItems: formValue.orderItems.map((item: any) => ({
        wineId: item.wineId,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        wineName: item.wineName,
        externalCode: item.externalCode
      }))
    } as any;

    this.httpWaitService.addFilter({ method: 'POST', endpoint: '/api/app/order/with-items' });

    this.orderService.createWithItems(input).subscribe({
      next: async (res: CreateOrderResponseDto) => {
        this.httpWaitService.removeFilter({ method: 'POST', endpoint: '/api/app/order/with-items' });
        if (res.hasStockIssues || !res.order) {
          await this.handleStockIssues(res.stockIssues);
          this.isLoading = false;
          this.isSaving = false;
          return;
        }
        this.toaster.success('Order created successfully');
        this.router.navigate(['/backoffice/orders']);
      },
      error: async (err) => {
        this.httpWaitService.removeFilter({ method: 'POST', endpoint: '/api/app/order/with-items' });
        this.isLoading = false;
        this.isSaving = false;
        if (this.isStockError(err)) {
          await this.handleStockIssues(err.error?.stockIssues || []); // Pass stock issues if available in error structure, else logic in isStockError might be enough to identify type but we need data.
          this.toaster.warn('::InsufficientStockWarning', '::StockWarning');
        } else {
          this.toaster.error(err.message || 'Error creating order');
        }
      }
    });
  }

  private async handleStockIssues(stockIssues: StockValidationResultDto[]): Promise<boolean> {
    // stockIssues puede venir vacío aunque el backend haya rechazado la orden por falta de
    // stock (caso "sin stock en ningún depósito" sin detalle por vino) — igual hay que avisar,
    // nunca tratar esto como si la orden se hubiera creado.
    const body = stockIssues?.length
      ? `${this.localization.instant('::StockModalUnavailableList')}<br>${stockIssues
          .map(i => {
            const availableQty = Math.floor(i.availableQuantity);
            if (i.status === StockStatus.Insufficient) {
              return `• ${i.wineName} (Stock disponible: ${availableQty} unidades)`;
            }
            return `• ${i.wineName}`;
          })
          .join('<br>')}<br><br>${this.localization.instant('::StockModalInstructions')}`
      : 'No se pudo crear la orden: no hay stock disponible para los vinos seleccionados en ningún depósito del sommelier.';

    await this.confirmationService.confirm({
      title: this.localization.instant('::StockWarningTitle'),
      message: body,
      type: 'warning',
      showCancelButton: false,
      confirmText: this.localization.instant('::Ok'),
    }).toPromise();

    return true;
  }

  /** Escapa un valor antes de interpolarlo en el HTML del modal de confirmación ([innerHTML]). */
  private escapeHtml(value: string): string {
    const map: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
    return value.replace(/[&<>"']/g, ch => map[ch]);
  }

  private isStockError(error: any): boolean {
    if (!error) return false;

    // Verificar en el mensaje de error
    const errorMessage = error.message || error.error?.message || '';
    if (typeof errorMessage === 'string' &&
      (errorMessage.toLowerCase().includes('stock') ||
        errorMessage.toLowerCase().includes('insufficient'))) {
      return true;
    }

    // Verificar en la descripción del error
    const errorDescription = error.error?.error?.message || '';
    if (typeof errorDescription === 'string' &&
      (errorDescription.toLowerCase().includes('stock') ||
        errorDescription.toLowerCase().includes('insufficient'))) {
      return true;
    }

    // Verificar en el código de error si existe
    const errorCode = error.error?.error?.code || error.code || '';
    if (typeof errorCode === 'string' &&
      (errorCode.toLowerCase().includes('stock') ||
        errorCode.toLowerCase().includes('insufficient'))) {
      return true;
    }

    return false;
  }
}
